import { useQuery, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, downloadAuthorized } from './client';
import { keys } from './keys';

const AuditModelSchema = z.object({
  mto_id: z.coerce.number().nullable().optional(),
  mto_name: z.string().nullable().optional(),
  model_id: z.coerce.number().nullable().optional(),
  model_name: z.string().nullable().optional(),
  catalog_line_count: z.coerce.number().nullable().optional(),
  line_count: z.coerce.number().optional(),
  issue: z.string().optional(),
});

const AuditLineSchema = z.object({
  lineId: z.coerce.number(),
  mtoId: z.coerce.number(),
  mtoName: z.string(),
  modelId: z.coerce.number().nullable().optional(),
  modelName: z.string().nullable().optional(),
  itemId: z.string().nullable().optional(),
  qty: z.coerce.number().nullable().optional(),
  payableTaskGroup: z.string().nullable().optional(),
  subPhaseName: z.string().nullable().optional(),
  supplier: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  hasPriceOverride: z.boolean().optional().default(false),
  trulyMissing: z.boolean().optional().default(true),
});

const CommunityAuditSchema = z.object({
  summary: z.object({
    modelsWithoutLines: z.coerce.number(),
    unmatchedLines: z.coerce.number(),
    missingSupplierPricing: z.coerce.number(),
  }),
  modelsWithoutLines: z.array(AuditModelSchema),
  unmatchedLines: z.array(AuditLineSchema),
  missingSupplierPricing: z.array(AuditLineSchema),
});

export type CommunityAudit = z.infer<typeof CommunityAuditSchema>;
export type CommunityAuditLine = z.infer<typeof AuditLineSchema>;
export type CommunityAuditModel = z.infer<typeof AuditModelSchema>;

export function invalidateCommunityAudit(
  qc: QueryClient,
  communityId: string | number | undefined
) {
  if (communityId == null || communityId === '') return;
  void qc.invalidateQueries({ queryKey: keys.communityAudit(communityId) });
}

export function invalidateAllCommunityAudits(qc: QueryClient) {
  void qc.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      query.queryKey[0] === 'communities' &&
      query.queryKey[2] === 'audit',
  });
}

export function useCommunityAudit(communityId: string | undefined) {
  return useQuery({
    queryKey: keys.communityAudit(communityId ?? ''),
    enabled: !!communityId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    queryFn: () => apiFetch(`/api/communities/${communityId}/audit`, CommunityAuditSchema),
  });
}

export const SUPPLIER_PRICING_REASON_LABELS: Record<string, string> = {
  no_item_id: 'No Item ID',
  no_sub_phase: 'No Sub-Phase Mapping',
  no_supplier: 'No Supplier Assigned',
  no_supplier_price: 'No Supplier Price',
};

export type CommunityAuditExportSection =
  | 'models-without-lines'
  | 'unmatched-lines'
  | 'missing-supplier-pricing';

const AUDIT_EXPORT_SECTION_FILENAMES: Record<CommunityAuditExportSection, string> = {
  'models-without-lines': 'models_without_lines',
  'unmatched-lines': 'unmatched_lines',
  'missing-supplier-pricing': 'missing_supplier_pricing',
};

function safeExportBasename(name: string): string {
  return (
    name
      .replace(/[^a-zA-Z0-9_\- ]/g, '')
      .trim()
      .replace(/\s+/g, '_') || 'community'
  );
}

export function buildCommunityAuditExportFilename(
  communityName: string,
  section: CommunityAuditExportSection
): string {
  return `${safeExportBasename(communityName)}_audit_${AUDIT_EXPORT_SECTION_FILENAMES[section]}.xlsx`;
}

export async function downloadCommunityAuditExport(
  communityId: string,
  section: CommunityAuditExportSection,
  communityName: string
) {
  await downloadAuthorized(
    `/api/communities/${communityId}/audit/export?section=${section}`,
    buildCommunityAuditExportFilename(communityName, section)
  );
}
